<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import radioRing from '@/assets/images/screening/radio-selected-ring.svg';
import radioDot from '@/assets/images/screening/radio-selected-dot.svg';
import radioOff from '@/assets/images/screening/radio-disabled.svg';

const props = defineProps({
	scales: {
		type: Array,
		default: () => [],
	},
	selectedId: {
		type: [Number, String],
		default: null,
	},
});

const emit = defineEmits(['select']);

const expanded = ref({});

const canSelect = (scale) =>
	scale?.state === 'completed' || scale?.state === 'published';

const scaleKey = (scale) =>
	`${scale?.name || ''}${scale?.code || ''}`
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

const SCALE_COPY = [
	{
		key: 'asq',
		step: 'Bước 1: Sàng lọc phát triển',
		title: 'ASQ-3',
		english: 'Ages & Stages Questionnaires, Third Edition',
		description:
			'Bộ câu hỏi kiểm tra mốc phát triển toàn diện của trẻ theo độ tuổi, giúp cha mẹ phát hiện sớm những mảng phát triển con đang cần được hỗ trợ thêm.',
		age: '1 - 66 tháng tuổi',
		when: 'Thực hiện khi cha mẹ muốn kiểm tra xem con có đang phát triển đúng độ tuổi về ngôn ngữ, vận động, tư duy và tương tác xã hội hay không.',
	},
	{
		key: 'mchat',
		step: 'Bước 2: Sàng lọc nguy cơ Tự kỷ',
		title: 'M-CHAT-R',
		english: 'Modified Checklist for Autism in Toddlers, Revised',
		description:
			'Bộ câu hỏi sàng lọc nguy cơ Rối loạn phổ tự kỷ dành cho trẻ nhỏ — công cụ sàng lọc được nêu trong Bộ công cụ phát hiện sớm của Bộ Y tế. Giúp cha mẹ nhận diện sớm các dấu hiệu nguy cơ để kịp thời đưa con đi đánh giá chuyên sâu.',
		age: '16 – 30 tháng tuổi',
		when: 'Nên dùng khi trẻ chậm nói, ít đáp lại khi được gọi tên, ít giao tiếp bằng mắt, chưa biết chỉ tay để chia sẻ điều trẻ quan tâm, hoặc khi kết quả ASQ-3 cho thấy con cần được theo dõi thêm ở mảng giao tiếp - xã hội.',
	},
	{
		key: 'cars',
		step: 'Bước 3: Đánh giá mức độ & biểu hiện',
		title: 'CARS',
		english: 'Childhood Autism Rating Scale',
		description:
			'Thang đánh giá dựa trên quan sát hành vi, giúp xác định mức độ biểu hiện liên quan đến Rối loạn phổ tự kỷ ở trẻ, từ nhẹ đến nặng — cơ sở quan trọng để xây dựng hướng can thiệp phù hợp cho con.',
		age: 'Từ 24 tháng tuổi',
		when: 'Nên dùng khi trẻ có các dấu hiệu chậm nói, ít tương tác mắt, hành vi lặp đi lặp lại, hoặc khi kết quả ASQ-3 / M-CHAT-R cho thấy con thuộc nhóm cần đánh giá chuyên sâu hơn.',
	},
	{
		key: 'dsm',
		step: 'Bước 4: Tiêu chuẩn chẩn đoán',
		title: 'DSM-5',
		english:
			'Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition',
		description:
			'Bảng tham chiếu các tiêu chuẩn chẩn đoán y khoa quốc tế đối với Rối loạn phổ tự kỷ - hệ tiêu chuẩn được bác sĩ và nhà lâm sàng sử dụng khi chẩn đoán xác định. Dành cho phụ huynh muốn tìm hiểu góc nhìn chuyên môn trước khi đưa con đi thăm khám chính thức.',
		age: 'Chẩn đoán thực hiện tại cơ sở chuyên môn',
		when: 'Dùng khi cha mẹ muốn hiểu rõ các nhóm tiêu chuẩn chẩn đoán chuẩn mực, và chuẩn bị thông tin đầy đủ trước khi đưa trẻ đến cơ sở chuyên môn để được đánh giá và kết luận chính thức.',
	},
];

const fallbackCopy = (scale) => ({
	step: '',
	title: scale?.name || '',
	english: scale?.full_english_name || '',
	description: scale?.description || '',
	age: '',
	when: '',
});

const displayScales = computed(() => {
	const used = new Set();
	const cards = SCALE_COPY.map((copy) => {
		const match = props.scales.find((scale) =>
			scaleKey(scale).includes(copy.key)
		);
		if (match) used.add(match.id);
		return {
			id: match ? match.id : `copy-${copy.key}`,
			api: match || null,
			copy,
			selectable: !!(match && canSelect(match)),
		};
	});
	props.scales.forEach((scale) => {
		if (used.has(scale.id)) return;
		cards.push({
			id: scale.id,
			api: scale,
			copy: fallbackCopy(scale),
			selectable: canSelect(scale),
		});
	});
	return cards;
});

const isSelected = (item) =>
	item.selectable && props.selectedId === item.id;

const selectScale = (item) => {
	if (!item.selectable) return;
	emit('select', item.id);
};

const toggleWhen = (id) => {
	expanded.value = {
		...expanded.value,
		[id]: !expanded.value[id],
	};
};
</script>

<template>
	<div class="screening-scales">
		<p class="screening-scales__lead">
			Chọn một trong 3 loại thang sàng lọc
		</p>
		<div
			v-for="item in displayScales"
			:key="item.id"
			class="screening-scale"
			:class="{
				'screening-scale--on': isSelected(item),
				'screening-scale--off': !item.selectable,
			}"
			@click="selectScale(item)"
		>
			<span class="screening-radio">
				<img
					class="screening-radio__ring"
					:src="item.selectable ? radioRing : radioOff"
					alt=""
				/>
				<img
					v-if="isSelected(item)"
					class="screening-radio__dot"
					:src="radioDot"
					alt=""
				/>
			</span>
			<span class="screening-scale__body">
				<p v-if="item.copy.step" class="screening-scale__step">
					{{ item.copy.step }}
				</p>
				<span class="screening-scale__heading">
					<span class="screening-scale__name-row">
						<span class="screening-scale__name">
							{{ item.copy.title }}
						</span>
						<span v-if="!item.selectable" class="screening-scale__soon">
							Sắp ra mắt
						</span>
					</span>
					<span v-if="item.copy.english" class="screening-scale__english">
						{{ item.copy.english }}
					</span>
				</span>
				<p v-if="item.copy.description" class="screening-scale__text">
					{{ item.copy.description }}
				</p>
				<span v-if="item.copy.age" class="screening-scale__age">
					{{ item.copy.age }}
				</span>
				<span v-if="item.copy.when" class="screening-scale__when">
					<span class="screening-scale__when-head">
						<span class="screening-scale__when-title">
							Khi nào nên dùng?
						</span>
						<button
							type="button"
							class="screening-scale__more"
							@click.stop="toggleWhen(item.id)"
						>
							{{ expanded[item.id] ? 'Thu gọn' : 'Xem thêm' }}
						</button>
					</span>
					<span
						v-if="expanded[item.id]"
						class="screening-scale__when-text"
					>
						{{ item.copy.when }}
					</span>
				</span>
			</span>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.screening-scales {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.screening-scales__lead {
	color: #65758b;
	font-size: 16px;
	line-height: 24px;
}

.screening-scale {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	width: 100%;
	padding: 16px;
	border: 1px solid transparent;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
	font-family: inherit;
	text-align: left;
	cursor: pointer;
}

.screening-scale--on {
	background: #fef8ed;
	border-color: #f0bb4f;
	box-shadow: none;
}

.screening-scale--off {
	cursor: default;
}

.screening-radio {
	position: relative;
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	margin-top: 2px;
}

.screening-radio__ring {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
}

.screening-radio__dot {
	position: absolute;
	inset: 15%;
	width: 70%;
	height: 70%;
}

.screening-scale__body {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 12px;
	min-width: 0;
	color: #344256;
}

.screening-scale__step,
.screening-scale__text,
.screening-scale__english,
.screening-scale__when-title,
.screening-scale__when-text {
	font-size: 16px;
	line-height: 24px;
}

.screening-scale__step,
.screening-scale__when-title {
	font-weight: 600;
}

.screening-scale__heading {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.screening-scale__name-row {
	display: flex;
	align-items: center;
	gap: 12px;
}

.screening-scale__name {
	font-size: 20px;
	font-weight: 600;
	line-height: 28px;
}

.screening-scale__soon {
	display: inline-flex;
	align-items: center;
	padding: 4px 16px;
	border-radius: 100px;
	background: #eef2ff;
	color: #6366f1;
	font-size: 12px;
	font-weight: 600;
	line-height: 16px;
}

.screening-scale__english {
	font-style: italic;
	font-weight: 300;
}

.screening-scale__age {
	display: inline-flex;
	align-items: center;
	width: fit-content;
	padding: 4px 12px 2px;
	border-radius: 100px;
	background: #fcf1da;
	color: #d89210;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
}

.screening-scale__when {
	display: flex;
	flex-direction: column;
	gap: 4px;
	width: 100%;
}

.screening-scale__when-head {
	display: flex;
	align-items: center;
	gap: 4px;
	width: 100%;
}

.screening-scale__when-title {
	flex: 1;
	min-width: 0;
}

.screening-scale__more {
	flex-shrink: 0;
	padding: 0;
	border: 0;
	background: transparent;
	color: #6390c3;
	font-family: inherit;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	text-decoration: underline;
	cursor: pointer;
}

.screening-scale__when-text {
	color: #344256;
}
</style>
